import React, { useState } from 'react';
import { FormattedDate, useIntl } from 'react-intl';
import { styled } from '@linaria/react';
import { ButtonLink } from '@daedalus/atlas/ButtonLink';
import { Stack } from '@daedalus/atlas/helpers/Stack';
import { Icon } from '@daedalus/atlas/Icon';
import { Text } from '@daedalus/atlas/Text';
import { Tooltip } from '@daedalus/atlas/Tooltip';
import { Page } from '@daedalus/core/src/analytics/types/Events';
import { BoardType } from '@daedalus/core/src/api-types/bovio/response/booking';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { RefundType } from '@daedalus/core/src/offer/business/cancellationPenalties';
import { CancellationPenalty } from '@daedalus/core/src/offer/types/offer';
import { CancellationLabel } from '../CancellationLabel';
import { ChargeLaterLabel } from '../ChargeLaterLabel';
import { Meal } from '../Meal';
import { CancellationPenaltiesPopUp } from './CancellationPenaltiesPopUp';
import { ChargeLaterDetailsPopUp } from './ChargeLaterPopUp';
import { shouldHideCancellationDeadline, shouldShowChargeLaterPerk, shouldShowChargeLaterRefundPolicy } from './utils';
const SOLD_OUT_COLOR = 'content.neutral.c400';
interface Props {
  /** Datetime of the cancellation deadline */
  freeCancellationDeadline?: string;
  /** Indicates if the user can be charged later for the offer. */
  payLater?: boolean;
  /** The type of meal/board included in the booking */
  boardType?: BoardType;
  /** A list of the cancellation penalties that are applied to the selected offer */
  cancellationPenalties: CancellationPenalty[];
  /** Total value of offer price */
  totalPrice: number;
  /** Currency code of offer price */
  currencyCode: string;
  /** Currency code of chargeable price */
  chargeableCurrencyCode?: string;
  /** Total upfront price */
  totalUpFrontDisplayPrice?: number | null;
  /** Total value of pay at property price */
  payAtPropertyPrice?: number;
  /** Perform optional actions when clicking cancellation penalties button */
  onCancellationButtonClick?: () => void;
  /** The hotel's timezone */
  hotelTimeZone?: string;
  /** Indicates if the offer is eligible for Charge Later */
  isChargeLaterEligible?: boolean;
  /** Indicates if the user has selected the charge later option in payment page */
  isChargeLaterSelected?: boolean;
  /** The date on which the user will be charged for the booking */
  chargeLaterDate?: Date;
  /**
   * Indicate if it's an existing charge later booking.
   */
  isChargeLaterBooking?: boolean;
  /**  The page where the component is rendered in */
  page?: Page;
  /** Callback for opening charge later details overlay */
  onChargeLaterLabelClicked?: () => void;
  /** Indicates the expected click action */
  chargeLaterClickAction?: 'bottomSheet' | 'tooltip';
  /** Whether the cancellation deadline should be hidden */
  hideFreeCancellationDeadlineDate?: boolean;
  /** Disables animation for bottom sheet */
  prefersReducedMotion?: boolean;
  /**
   * Indicates if the component is rendered in a post booking page.
   */
  isPostBooking?: boolean;

  /** Type of cancellation penalty (e.g., non-refundable, partial, free cancellation) */
  refundType: RefundType;
  /** Applies sold-out styling to visually indicate that the offer is no longer available. */
  isOfferSoldOut?: boolean;
}
const Row = /*#__PURE__*/styled('div')({
  name: "Row",
  class: "r1p8a0ip",
  propsAsIs: false
});
const _exp2 = /*#__PURE__*/() => Tooltip;
const StyledTooltip = /*#__PURE__*/styled(_exp2())({
  name: "StyledTooltip",
  class: "s7ybkb2",
  propsAsIs: true
});
const _exp5 = /*#__PURE__*/() => Text;
const DottedText = /*#__PURE__*/styled(_exp5())({
  name: "DottedText",
  class: "d1nl703u",
  propsAsIs: true
});
interface ChargeLaterTooltipProps {
  chargeDate: Date;
  children: React.ReactNode;
}
const ChargeLaterTooltip = ({
  chargeDate,
  children
}: ChargeLaterTooltipProps) => {
  const {
    formatMessage
  } = useIntl();
  const title = formatMessage({
    id: 'chargeLaterTooltip.title',
    defaultMessage: 'Book now, pay later. Zero risk.'
  });
  const formattedChargeDate = <FormattedDate value={chargeDate} month="short" day="2-digit" />;
  const content = formatMessage({
    id: 'chargeLaterTooltip.description',
    defaultMessage: 'We’ll secure your booking today, then charge you on {chargeDate}. Cancel for free before then.'
  }, {
    chargeDate: formattedChargeDate
  });
  return <StyledTooltip title={title} content={content} isInline={false}>
      <DottedText>{children}</DottedText>
    </StyledTooltip>;
};
export const OfferDetails = ({
  freeCancellationDeadline,
  payLater,
  boardType,
  cancellationPenalties,
  totalPrice,
  totalUpFrontDisplayPrice,
  currencyCode,
  chargeableCurrencyCode,
  payAtPropertyPrice,
  hotelTimeZone,
  onCancellationButtonClick,
  isChargeLaterEligible,
  isChargeLaterSelected,
  chargeLaterDate,
  isChargeLaterBooking,
  page,
  onChargeLaterLabelClicked,
  chargeLaterClickAction = 'bottomSheet',
  hideFreeCancellationDeadlineDate,
  prefersReducedMotion,
  isPostBooking,
  refundType,
  isOfferSoldOut
}: Props) => {
  const [isCancellationPenaltiesDetailsOpen, setIsCancellationPenaltiesDetailsOpen] = useState(false);
  const [isChargeLaterDetailsOpen, setIsChargeLaterDetailsOpen] = useState(false);
  const onCancellationLabelClick = () => {
    onCancellationButtonClick?.();
    setIsCancellationPenaltiesDetailsOpen(true);
  };
  const onChargeLaterLabelClick = () => {
    onChargeLaterLabelClicked?.();
    setIsChargeLaterDetailsOpen(true);
  };
  const showChargeLaterPerk = shouldShowChargeLaterPerk({
    page,
    isChargeLaterSelected,
    isChargeLaterEligible,
    isChargeLaterBooking,
    chargeLaterDate
  });
  const hideCancellationDeadline = hideFreeCancellationDeadlineDate || shouldHideCancellationDeadline({
    isChargeLaterEligible,
    isChargeLaterBooking
  });
  const showChargeLaterRefundPolicy = shouldShowChargeLaterRefundPolicy({
    page,
    isChargeLaterSelected,
    isChargeLaterEligible,
    isChargeLaterBooking
  });
  const soldOutColor = isOfferSoldOut ? SOLD_OUT_COLOR : undefined;
  return <Stack space="s200">
      <Row>
        {isOfferSoldOut ? <CancellationLabel isOfferSoldOut={isOfferSoldOut} refundType={refundType} freeCancellationDeadline={freeCancellationDeadline} hideFreeCancellationDeadlineDate={hideCancellationDeadline} variant="roomDetails" /> : <ButtonLink target="_blank" onClick={onCancellationLabelClick} rel="noreferrer" size="sm" isCompact variant="info" isDotted>
            <CancellationLabel refundType={refundType} freeCancellationDeadline={freeCancellationDeadline} hideFreeCancellationDeadlineDate={hideCancellationDeadline} variant="roomDetails" />
          </ButtonLink>}

        <CancellationPenaltiesPopUp cancellationPenalties={cancellationPenalties} hotelTimeZone={hotelTimeZone} totalPrice={totalPrice} currencyCode={currencyCode} chargeableCurrencyCode={chargeableCurrencyCode} setIsCancellationPenaltiesDetailsOpen={setIsCancellationPenaltiesDetailsOpen} isCancellationPenaltiesDetailsOpen={isCancellationPenaltiesDetailsOpen} hasChargeLaterRefundPolicy={showChargeLaterRefundPolicy} prefersReducedMotion={prefersReducedMotion} isPostBooking={isPostBooking} />
      </Row>

      {showChargeLaterPerk && chargeLaterDate && (isOfferSoldOut ? <ChargeLaterLabel isOfferSoldOut={isOfferSoldOut} /> : <Row>
            {chargeLaterClickAction === 'tooltip' && <ChargeLaterTooltip chargeDate={chargeLaterDate}>
                <ChargeLaterLabel />
              </ChargeLaterTooltip>}

            {chargeLaterClickAction === 'bottomSheet' && <ButtonLink target="_blank" onClick={onChargeLaterLabelClick} rel="noreferrer" size="sm" isCompact variant="info" isDotted>
                <ChargeLaterLabel />
              </ButtonLink>}

            <ChargeLaterDetailsPopUp chargeDate={chargeLaterDate} setIsChargeLaterDetailsOpen={setIsChargeLaterDetailsOpen} isChargeLaterDetailsOpen={isChargeLaterDetailsOpen} currencyCode={currencyCode} cancellationPenalties={cancellationPenalties} payAtPropertyPrice={payAtPropertyPrice} totalUpFrontDisplayPrice={totalUpFrontDisplayPrice} hotelTimeZone={hotelTimeZone} />
          </Row>)}

      {boardType && boardType !== BoardType.RoomOnly && <Meal boardType={boardType} iconColor={soldOutColor} textColor={soldOutColor} />}
      {payLater && <Row>
          <Icon name="Wallet" colorPath={isOfferSoldOut ? SOLD_OUT_COLOR : 'content.neutral.c600'} size="sm" />

          <Text variant="bodyS" colorPath={isOfferSoldOut ? SOLD_OUT_COLOR : 'content.neutral.c950'} dataId="payLater">
            <FormattedMessageWrapper id="paymentSchedule.payAtProperty" defaultMessage="Pay at property" />
          </Text>
        </Row>}
    </Stack>;
};

require("./OfferDetails.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./OfferDetails.tsx");